import React, { useCallback, useEffect, useMemo } from 'react';
import { EOverlaySelectionType } from 'enums/EOverlaySelectionType';
import { hasModifyAccess } from 'utils/accessRights';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { IStyle } from 'Styles';
import './style.scss';
import SideBarComponent from '../SideBarComponent/SideBarComponent';
import {
  selectExp,
  selectIsDropImageAvailable,
} from 'redux/selectors/ExperimentStateSelector';
import {
  setSelectOverlay,
  togglePlateImaging,
} from 'redux/reducers/experimentDataReducer';
import ImageTuning from 'components/commandpanel/ImageTuning/ImageTuning';
import {
  setSelectedDrawing,
  setIsWellImagingEnabled,
} from 'redux/reducers/experimentReducer';
import DrawingTools from 'components/sidePanel/searchAndFilter/imageTools/drawingTools';
import DrawingCreationTools from 'components/sidePanel/searchAndFilter/imageTools/drawingCreationTools';
import ImagingToggles, { ImagingToggleProps } from './ImagingTools';
import {
  useExperimentImagingDisabledState,
  useIsWellImagingEnabled,
  usePlateImagingState,
  useToggleWellDropImage,
} from 'components/Experiment/hooks';
import { PlatesImagingTaskDTO } from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import { useCurrentDropImage, useWellDrawingStatus } from 'redux/hooks/well';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { WellDetailsStates } from 'business/models/CustomModels';
import { setDrawingTool } from 'redux/reducers/wellReducer';
import { OverlayTools } from 'components/well/drawingManager/constants';
import { useFetchDrawingPreferences } from 'redux/hooks/searchnFilter';

const ExperimentTools = () => {
  const exp = useRMSelector(selectExp);
  const isDropImageAvailable = useRMSelector(selectIsDropImageAvailable);
  const dispatch = useRMDispatch();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { plateID, plateNum, wellNum } = wellNavigationDetails || {};
  const { dropNumber, regionName, cpId, inspection } = selectedDropImageParameters || {};
  const drawingsStatus = useWellDrawingStatus(plateID, wellNum, dropNumber, regionName);
  const dropImage = useCurrentDropImage();
  const fetchDrawingPreferences = useFetchDrawingPreferences();
  const wellDetails = useMemo(
    () =>
      ({
        wellNum,
        plateID,
        plateNum,
        cpID: cpId,
        dropNumber,
        regionName,
        inspectionSet: inspection,
      } as WellDetailsStates),
    [],
  );
  const setSelectedOverlay = useCallback(
    (data) => dispatch(setSelectOverlay(data)),
    [dispatch],
  );
  const _setSelectedDrawing = (data) => {
    dispatch(setSelectedDrawing(data));
  };
  const isWellImagingEnabled = useIsWellImagingEnabled(dropNumber);
  const experimentImagingDisabled = useExperimentImagingDisabledState();
  const plateImagingEnabled = usePlateImagingState();
  const toggleWellDropImaging = useToggleWellDropImage();

  const toggleExperimentImagingState = async () => {
    const plateIds = exp?.plateDto?.map((p) => p.plateId);
    const plates = plateIds.map(
      (p) =>
        new PlatesImagingTaskDTO({ plateId: p, imagingEnabled: !plateImagingEnabled }),
    );
    dispatch(togglePlateImaging({ plates }));
    dispatch(setIsWellImagingEnabled(!isWellImagingEnabled));
    await new ExperimentService().setExperimentImaging(
      exp.experimentId,
      experimentImagingDisabled,
    );
  };

  const imagingToggles: ImagingToggleProps[] = [
    {
      label: 'Imaging Experiment',
      isOn: experimentImagingDisabled,
      onToggle: toggleExperimentImagingState,
      disabled: false,
    },
    {
      label: 'Imaging Drop',
      isOn: !isWellImagingEnabled,
      onToggle: toggleWellDropImaging,
      disabled: experimentImagingDisabled || !plateImagingEnabled,
    },
  ];

  const hasOverview = useMemo(() => {
    return exp?.regions?.some((r) =>
      r?.drops?.some(
        (d) =>
          d.plateId === plateID &&
          d.wellNumber === wellNum &&
          d.dropNumber === dropNumber &&
          d.regionName === 'Overview',
      ),
    );
  }, [exp, plateID, wellNum, dropNumber]);

  useEffect(() => {
    fetchDrawingPreferences();
    return () => {
      setSelectedOverlay(EOverlaySelectionType.POINTER);
      _setSelectedDrawing(null);
      dispatch(setDrawingTool(OverlayTools.POINTER));
    };
  }, []);

  return (
    <div id="commandpanel-exptools-mainhead" style={styles.mainHead}>
      <div id="commandpanel-exptools-container" className="flex flex-col">
        <DrawingCreationTools
          wellDetails={wellDetails}
          expId={exp.experimentId}
          isModifyAccess={hasModifyAccess(exp?.accessRightName)}
          imagePixelSize={dropImage?.pixelSize}
          isDropImageAvailable={isDropImageAvailable}
          regionName={regionName}
          hasOverview={hasOverview}
          drawingsStatus={drawingsStatus}
        />
        <div id="commandpanel-exptools-disableimaging" style={styles.tools}>
          <SideBarComponent
            heading={'Disable Imaging'}
            isCollapsible={true}
            handleColapse={() => {}}
            ContainerComponent={
              <div id="commandpanel-exptools-imagingtoggles" style={styles.toolsHeading}>
                <ImagingToggles toggles={imagingToggles} />
              </div>
            }
          />
        </div>
      </div>
      <div id="commandpanel-exptools-tooloptions" style={styles.tools}>
        <SideBarComponent
          heading={'Tool Options'}
          isCollapsible={true}
          handleColapse={() => {}}
          ContainerComponent={<DrawingTools />}
        />
      </div>
      <div id="commandpanel-exptools-imagetuning" style={styles.tools}>
        <SideBarComponent
          heading={'Image Tuning'}
          isCollapsible={true}
          handleColapse={() => {}}
          ContainerComponent={<ImageTuning />}
        />
      </div>
    </div>
  );
};
const styles: IStyle = {
  mainHead: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflowY: 'scroll',
    overflowX: 'hidden',
    height: '96%',
  },
  tools: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  toolsHeading: {
    paddingBlock: '2%',
    paddingInline: '1%',
  },
  toolsContent: {
    paddingBlock: '2%',
    paddingInline: '1%',
  },
};
export default ExperimentTools;
